import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { IIncidentModalComponent } from '@max/incident/i-incident-modal.component';
import { toDateOrNull, toNumberOrNull, toStringOrNull } from '@max/incident/shared/data-helpers';
import { BaseComponent } from '@max/shared/base-components/base.component';
import { MvdModalService } from '@max/shared/services/modal/mvd-modal.service';
import * as moment from 'moment';
import { forkJoin, Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { IncidentModalComponent } from '../../incident-modal.component';
import { IncidentConvictionOojModel } from '../../models/incident-conviction-ooj.model';
import { ConvictionIncidentOojService } from '../../services/conviction-incident-ooj.service';
import { IncidentService } from '../../services/incident.service';
import { IncidentDialogContext } from '../../shared/incident-dialog-context.model';

@Component({
    selector: 'conviction-incident-ooj-modal',
    templateUrl: './conviction-incident-ooj-modal.html'
})
export class OojConvictionIncidentModalComponent extends BaseComponent implements IIncidentModalComponent<IncidentConvictionOojModel> {
    public form: UntypedFormGroup;
    public busy = false;
    public incidentTypeId: number = null;
    public citingAgencyTypes: any[] = [];
    public dispositionCodeTypes: any[] = [];
    public accidentTypes: any[] = [];
    public courtOrderedInterlockMonthsTypes: any[] = [];
    public courtActionFlagTypes: any[] = [];
    public courtActionCodeTypes: any[] = [];
    public courtOrderedActionTypes: any[] = [];
    public civilCriminalViolationTypes: any[] = [];
    public drugViolationTypeTypes: any[] = [];
    public courts: any[] = [];
    public plateTypes: any[] = [];
    public vehicleJurisdictions: any[] = [];
    public acdCodes: any[] = [];
    public courtTypes: any[] = [];
    public oojJurisdictions: any[] = [];
    public incidentTypes: any[] = [];
    public disableCheckAddIncidentWOWithdrawal = false;
    public bacRequired = false;
    public underlyingAcdCodeRequired = false;
    private derivedCdl = false;
    private csrCdlOverride = false;
    private disableCdlValidation = false;
    public isExistingIncident = false;

    constructor(
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public data: IncidentDialogContext<IncidentConvictionOojModel>,
        public oojConvictionIncidentService: ConvictionIncidentOojService,
        public dialog: MatDialogRef<IncidentModalComponent<IncidentConvictionOojModel>>,
        public incidentService: IncidentService,
        private modalService: MvdModalService,
        location: PlatformLocation
    ) {
        super();
        location.onPopState(() => this.dialog.close());
    }

    public ngOnInit(): void {
    }

    public initLookups = (lookups: any): Observable<any> => forkJoin({
        incidentTypes: this.incidentService.getIncidentTypes(),
        lookups: this.oojConvictionIncidentService.getLookups(),
        cdlStatus: this.incidentService.getCDLStatusOnDate(this.data.incident)
    }).pipe(map(r => this.loadLookups(r)));

    public closeModal = (result?: any): void => {
        this.dialog.close(result);
    };

    public populateModel = (form: UntypedFormGroup, model: IncidentConvictionOojModel): void => {
        model.driverLicenseNumber = toStringOrNull(form.get('driverLicenseNumber').value);
        model.driverLicenseJurisdictionId = toNumberOrNull(form.get('driverLicenseJurisdictionId').value);
        model.vehicleLicensePlateNumber = toStringOrNull(form.get('vehicleLicensePlateNumber').value);
        model.plateTypeId = toNumberOrNull(form.get('plateTypeId').value);
        model.vehicleRegistrationJurisdictionId = toNumberOrNull(form.get('vehicleRegistrationJurisdictionId').value);
        model.vehicleMake = toStringOrNull(form.get('vehicleMake').value);
        model.courtTypeId = toNumberOrNull(form.get('courtTypeId').value);
        model.courtName = toStringOrNull(form.get('courtName').value);
        model.courtStreetAddress = toStringOrNull(form.get('courtStreetAddress').value);
        model.courtCity = toStringOrNull(form.get('courtCity').value);
        model.courtJurisdictionId = toNumberOrNull(form.get('courtJurisdictionId').value);
        model.courtZipCode = toStringOrNull(form.get('courtZipCode').value);
        model.courtTelephoneNumber = toStringOrNull(form.get('courtTelephoneNumber').value);
        model.reportingJurisdictionId = toNumberOrNull(form.get('reportingJurisdictionId').value);
        model.dispositionDate = toDateOrNull(form.get('dispositionDate').value);
        model.stateNativeCode = toStringOrNull(form.get('stateNativeCode').value);
        model.approximateSpeed = toNumberOrNull(form.get('approximateSpeed').value);
        model.lawfulSpeed = toNumberOrNull(form.get('lawfulSpeed').value);
        model.bac = toNumberOrNull(form.get('bac').value);
        model.accidentTypeId = toNumberOrNull(form.get('accidentTypeId').value);
        model.underlyingAcdCodeId = toNumberOrNull(form.get('underlyingAcdCodeId').value);
        model.complaintNumber = toStringOrNull(form.get('complaintNumber').value);
        model.cdlHolderIndicator = true == form.get('isCDLHolder').value;
        model.csrCdlOverride = this.csrCdlOverride;
        model.commercialMotorVehicleIndicator = true == form.get('commercialMotorVehicleIndicator').value;
        model.hazardousMaterialIndicator = true == form.get('hazardousMaterialIndicator').value;
        model.passengerIndicator = true == form.get('passengerIndicator').value;
        model.isEventWOProhibition = true == this.form.get('isEventWOProhibition').value;
    };

    public formInitialized(form: UntypedFormGroup): void {
        this.form = form;
        form.addControl('driverLicenseNumber', new UntypedFormControl('', Validators.required));
        form.addControl('driverLicenseJurisdictionId', new UntypedFormControl(null, Validators.required));
        form.addControl('vehicleLicensePlateNumber', new UntypedFormControl(''));
        form.addControl('plateTypeId', new UntypedFormControl(null));
        form.addControl('vehicleRegistrationJurisdictionId', new UntypedFormControl(null));
        form.addControl('vehicleMake', new UntypedFormControl(''));
        form.addControl('courtTypeId', new UntypedFormControl(null, Validators.required));
        form.addControl('courtName', new UntypedFormControl(''));
        form.addControl('courtStreetAddress', new UntypedFormControl(''));
        form.addControl('courtCity', new UntypedFormControl(''));
        form.addControl('courtJurisdictionId', new UntypedFormControl(null, Validators.required));
        form.addControl('courtZipCode', new UntypedFormControl(''));
        form.addControl('courtTelephoneNumber', new UntypedFormControl(''));
        form.addControl('reportingJurisdictionId', new UntypedFormControl(null, Validators.required));
        form.addControl('dispositionDate', new UntypedFormControl(null, Validators.required));
        form.addControl('stateNativeCode', new UntypedFormControl('', Validators.required));
        form.addControl('approximateSpeed', new UntypedFormControl(''));
        form.addControl('lawfulSpeed', new UntypedFormControl(''));
        form.addControl('bac', new UntypedFormControl(''));
        form.addControl('accidentTypeId', new UntypedFormControl(null, Validators.required));
        form.addControl('underlyingAcdCodeId', new UntypedFormControl(null));
        form.addControl('complaintNumber', new UntypedFormControl(''));
        form.addControl('isCDLHolder', new UntypedFormControl(false));
        form.addControl('commercialMotorVehicleIndicator', new UntypedFormControl(false));
        form.addControl('hazardousMaterialIndicator', new UntypedFormControl(false));
        form.addControl('passengerIndicator', new UntypedFormControl(false));
        form.get('isCDLHolder').valueChanges.pipe(takeUntil(this.componentDestroyed)).subscribe(v => this.isCdlHolderChanged(v));

        if (null != this.incidentTypeId) {
            this.updateFormValidationRules(form, this.incidentTypeId);
        }

        form.setValidators(this.underlyingACDCodeValidator);
    }

    public updateFormValidationRules = (form: UntypedFormGroup, incidentTypeId: number): void => {
        this.incidentTypeId = incidentTypeId;
        this.underlyingAcdCodeRequired = this.isUnderlyingAcdRequired(incidentTypeId);

        if (true == this.bacRequired) {
            form.get('bac').setValidators(Validators.required);
        }
    };

    public updateData = (form: UntypedFormGroup, model: IncidentConvictionOojModel): void => {
        form.get('driverLicenseNumber').setValue(model.driverLicenseNumber);
        form.get('driverLicenseJurisdictionId').setValue(toNumberOrNull(model.driverLicenseJurisdictionId));
        form.get('vehicleLicensePlateNumber').setValue(toStringOrNull(model.vehicleLicensePlateNumber));
        form.get('plateTypeId').setValue(toNumberOrNull(model.plateTypeId));
        form.get('vehicleRegistrationJurisdictionId').setValue(toNumberOrNull(model.vehicleRegistrationJurisdictionId));
        form.get('vehicleMake').setValue(toStringOrNull(model.vehicleMake));
        form.get('courtTypeId').setValue(toNumberOrNull(model.courtTypeId));
        form.get('courtName').setValue(toStringOrNull(model.courtName));
        form.get('courtStreetAddress').setValue(toStringOrNull(model.courtStreetAddress));
        form.get('courtCity').setValue(toStringOrNull(model.courtCity));
        form.get('courtJurisdictionId').setValue(toNumberOrNull(model.courtJurisdictionId));
        form.get('courtZipCode').setValue(toStringOrNull(model.courtZipCode));
        form.get('courtTelephoneNumber').setValue(toStringOrNull(model.courtTelephoneNumber));
        form.get('reportingJurisdictionId').setValue(toNumberOrNull(model.reportingJurisdictionId));
        form.get('dispositionDate').setValue(toDateOrNull(model.dispositionDate));
        form.get('stateNativeCode').setValue(toStringOrNull(model.stateNativeCode));
        form.get('approximateSpeed').setValue(toNumberOrNull(model.approximateSpeed));
        form.get('lawfulSpeed').setValue(toNumberOrNull(model.lawfulSpeed));
        form.get('bac').setValue(toNumberOrNull(model.bac));
        form.get('accidentTypeId').setValue(toNumberOrNull(model.accidentTypeId));
        form.get('underlyingAcdCodeId').setValue(toNumberOrNull(model.underlyingAcdCodeId));
        form.get('complaintNumber').setValue(toStringOrNull(model.complaintNumber));
        form.get('commercialMotorVehicleIndicator').setValue(true == model.commercialMotorVehicleIndicator);
        form.get('hazardousMaterialIndicator').setValue(true == model.hazardousMaterialIndicator);
        form.get('passengerIndicator').setValue(true == model.passengerIndicator);

        if (!!model.id) {
            // DRIVER_TODO: Tech debt #159400
            this.setCdlHolderValue(true == model.cdlHolderIndicator);
            this.isExistingIncident = true;
        }
    };

    public getIncident = (incidentNumberId: number): Observable<IncidentConvictionOojModel> => this.oojConvictionIncidentService.getIncident(incidentNumberId);

    public save = (model: IncidentConvictionOojModel): Observable<any> => this.oojConvictionIncidentService.saveIncident(model);

    public loadComplete = (): void => {
        if (this.isNRVCACDCode(this.incidentTypeId)) {
            let addIncidentWoWithdrawalControl = this.form.get('isEventWOProhibition');
            addIncidentWoWithdrawalControl.setValue(true);
            addIncidentWoWithdrawalControl.disable();
        }

        // Now that we have lookups retrieved, we attempt to update form validation since it relies on lookup data
        this.updateFormValidationRules(this.form, this.data.incident.incidentTypeId);
    };

    private loadLookups = (results: any): void => {
        this.evaluateDriverCdl(results.cdlStatus);
        this.incidentTypes = results.incidentTypes.result;
        let lookups = results.lookups.result;
        let incidentType = this.incidentTypes.find(x => x.id == this.data.incident.incidentTypeId);
        this.citingAgencyTypes = lookups.citingAgencyTypes;
        this.dispositionCodeTypes = lookups.dispositionCodeTypes;
        this.accidentTypes = lookups.accidentTypes;
        this.courtOrderedInterlockMonthsTypes = lookups.courtOrderedInterlockMonthsTypes;
        this.courtActionFlagTypes = lookups.courtActionFlagTypes;
        this.courtActionCodeTypes = lookups.courtActionCodeTypes;
        this.courtOrderedActionTypes = lookups.courtOrderedActionTypes;
        this.civilCriminalViolationTypes = lookups.civilCriminalViolationTypes;
        this.drugViolationTypeTypes = lookups.drugViolationTypeTypes;
        this.courts = lookups.courts;
        this.plateTypes = lookups.plateTypes;
        this.vehicleJurisdictions = lookups.vehicleJurisdictions;

        // Filter out the base jurisdiction
        let baseJurisdiction = this.getBaseJurisdiction();
        this.oojJurisdictions = lookups.vehicleJurisdictions.filter(x => x.value != baseJurisdiction.id);

        this.courtTypes = lookups.courtTypes;
        this.bacRequired = 4 == incidentType.acdCodeId;

        if (incidentType.id == this.incidentTypes.find(x => x.incidentTypeValue == 'NRVCViolation').id) {
            this.acdCodes = lookups.acdCodes.filter(x => x.text.substring(0, 1) == 'M' || x.text.substring(0, 1) == 'N' || x.text.substring(0, 1) == 'S');
        } else {
            this.acdCodes = lookups.acdCodes;
        }

        // Now that we have lookups retrieved, we attempt to update form validation since it relies on lookup data
        this.updateFormValidationRules(this.form, this.data.incident.incidentTypeId);
    };

    private isUnderlyingAcdRequired = (incidentTypeId: number): boolean => {
        let incidentType = this.incidentTypes.find(x => x.id == incidentTypeId);

        if (this.incidentTypes.find(x => x.id == incidentTypeId)) {

            let occurrenceDate = this.form.get('occurrenceDate')
                && this.form.get('occurrenceDate').value
                && moment(this.form.get('occurrenceDate').value);

            if (incidentType.id == this.incidentTypes.find(x => x.incidentTypeValue == 'NRVCViolation').id
                || (occurrenceDate
                    && occurrenceDate.toDate() >= new Date(2005, 8, 30)
                    && (incidentType.id == this.incidentTypes.find(x => x.incidentTypeValue == 'D45').id
                        || incidentType.id == this.incidentTypes.find(x => x.incidentTypeValue == 'D53').id
                        || incidentType.id == this.incidentTypes.find(x => x.incidentTypeValue == 'D56').id))
            ) {
                return true;
            } else {
                return false;
            }
        }
    };

    private isNRVCACDCode = (incidentTypeId: number): boolean => {
        let incidentType = this.incidentTypes.find(x => x.id == incidentTypeId);

        if (null != incidentType) {
            return (incidentType.id == this.incidentTypes.find(x => x.incidentTypeValue == 'D56').id);
        }

        return false;
    };

    private evaluateDriverCdl = (checkCdl: any): void => {
        this.derivedCdl = true == checkCdl.result;
        this.setCdlHolderValue(this.derivedCdl);
    };

    private setCdlHolderValue = (value: boolean): void => {
        this.disableCdlValidation = true;

        try {
            this.form.get('isCDLHolder').setValue(value);
        } finally {
            this.disableCdlValidation = false;
        }
    };

    private isCdlHolderChanged = (value: boolean): void => {
        if (true == this.disableCdlValidation) {
            return;
        }

        this.csrCdlOverride = false;

        if ((null != this.derivedCdl) && (value != this.derivedCdl)) {
            this.modalService.confirm()
                .title('CDL Holder Confirmation')
                .body('MAX has derived a different value for CDL Holder indicator than what you have selected, do you want to continue?')
                .okBtn('Yes')
                .cancelBtn('No')
                .open()
                .afterClosed()
                .subscribe(r => {
                    if (false == r) {
                        this.setCdlHolderValue(true == this.derivedCdl);
                    } else {
                        this.csrCdlOverride = true;
                    }
                });
        }
    };

    private underlyingACDCodeValidator = (group: any): { [key: string]: boolean } => {
        if ((!group.get('underlyingAcdCodeId').value || group.get('underlyingAcdCodeId').value == 'null') && this.isUnderlyingAcdRequired(this.incidentTypeId)) {
            return { 'nullUnderlyingAcdCodeId': true };
        }
    };
}
